import React, { useEffect, useState } from 'react';
import { useToast, ToastMessage } from '../contexts/ToastContext';
import { CheckCircle2, AlertTriangle, AlertCircle, Info, X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { cn } from '../lib/utils';

export function ToastContainer() {
  const { toasts } = useToast();

  return (
    <div className="fixed bottom-0 left-0 right-0 p-4 md:p-6 md:left-auto md:right-0 md:top-auto z-[100] pointer-events-none flex flex-col gap-3 max-w-[400px] items-center md:items-end mx-auto md:mx-0 w-full">
      <AnimatePresence initial={false}>
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} />
        ))}
      </AnimatePresence>
    </div>
  );
}

function ToastItem({ toast }: { toast: ToastMessage }) {
  const { removeToast } = useToast();
  const [isUndoing, setIsUndoing] = useState(false);

  const handleUndo = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (toast.onUndo && !isUndoing) {
      setIsUndoing(true);
      try {
        await toast.onUndo();
      } catch (err) {
        console.error('Undo failed:', err);
      } finally {
        removeToast(toast.id);
      }
    }
  };

  const getIcon = () => {
    switch (toast.type) {
      case 'success': return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'error': return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'warning': return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      case 'info': return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.2 } }}
      transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
      className={cn(
        "pointer-events-auto w-full md:w-auto md:min-w-[320px] rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-[#e5e7eb]/60 flex items-center justify-between overflow-hidden relative",
        "bg-white/70 backdrop-blur-xl supports-[backdrop-filter]:bg-white/60"
      )}
    >
      <div className="flex items-center p-3.5 pr-4 gap-3">
        <div className="flex-shrink-0">
          {getIcon()}
        </div>
        <p className="text-[15px] font-medium text-gray-800 tracking-tight">
          {toast.message}
        </p>
      </div>

      <div className="flex items-center pr-2 shrink-0">
        {toast.onUndo && (
          <button
            onClick={handleUndo}
            disabled={isUndoing}
            className="px-3 py-1.5 mr-1 text-sm font-semibold text-blue-600 hover:bg-blue-50/50 rounded-lg transition-colors disabled:opacity-50"
          {...({} as any)}
          >
            {isUndoing ? 'Undoing...' : 'Undo'}
          </button>
        )}
        <button
          onClick={() => removeToast(toast.id)}
          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100/50 rounded-lg transition-colors"
          {...({} as any)}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
}
